import React, {useMemo} from "react"

import {hangPromise} from "../promise"

/** all states imply some presense of local keys except `fetching` which means the bucket is being fetched for the first time */
export type BucketState = "local" | "fetching" | "updating" | "latest"

export type LangLoaderContext = {
  getContent: (buckets: string[], locale: string) => Promise<any>
  bucketStates: Record<string, BucketState>
}

const langLoaderContext = React.createContext<null | LangLoaderContext>(null)

export const LangLoaderProvider = React.memo(langLoaderContext.Provider)

/**
 * hook to fetch phrase keys from cms
 * NOTE: this hook acts a means of triggering the fetch, but it does not provde their values.
 *
 * Instead the keys will be made availble via the `IntlProvider` context and
 * access from relevant components such as `<FormattedMessage />`
 */
export function useLangLoader(buckets: string[]) {
  const ctx = React.useContext(langLoaderContext)
  if (!ctx) {
    throw new Error("useLangLoader must be used inside the LangProvider")
  }
  const {getContent, bucketStates} = ctx

  // here we are asserting we have at least some kind of value to render to the user
  // if so we can continue to fetch the keys in the background
  const allLoaded =
    !!buckets.length &&
    buckets.map((b) => b.toLowerCase()).every((bucket) => ["local", "updating", "latest"].includes(bucketStates[bucket]))

  return useMemo(
    () =>
      [
        getContent,
        {
          loading: !allLoaded,
          load: allLoaded ? Promise.resolve() : hangPromise()
        }
      ] as const,
    [allLoaded, getContent]
  )
}
